import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, Handshake, Heart, Leaf, ShieldCheck, Users } from 'lucide-react';
import { useLang } from '../i18n';
import { Reveal } from './Reveal';
import { SectionHeader } from './SectionHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = [
  { icon: Users, keys: 'pil.1' },
  { icon: Heart, keys: 'pil.2' },
  { icon: Leaf, keys: 'pil.3' },
  { icon: ShieldCheck, keys: 'pil.4' },
];

export const Partners = () => {
  const { t } = useLang();
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    fetch(`${API}/partners`)
      .then((r) => r.json())
      .then((d) => setPartners(Array.isArray(d) ? d : []))
      .catch(() => setPartners([]));
  }, []);

  return (
    <section id="parceiros" data-testid="partners-section" className="scroll-mt-20 bg-white py-24 md:py-32">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <SectionHeader align="center" eyebrow={t('partners.kicker')} title={t('partners.title')} text={t('partners.sub')} />

        {partners.length > 0 && (
          <Reveal delay={0.1} className="mt-10 flex flex-wrap justify-center gap-3">
            {partners.map((p) => (
              <Link
                key={p.id}
                to={`/partners/${p.id}`}
                data-testid={`home-partner-${p.id}`}
                className="flex items-center gap-2.5 rounded-full border border-eco-ink/10 bg-eco-cream/70 py-2 pr-5 pl-2 transition-all duration-300 hover:border-eco-gold/50 hover:shadow-md"
              >
                <span className="grid h-8 w-8 place-items-center rounded-full bg-eco-green font-serif text-sm text-eco-gold">{p.name.charAt(0)}</span>
                <span className="text-sm font-medium text-eco-ink">{p.name}</span>
                <BadgeCheck size={13} className="text-eco-green" />
              </Link>
            ))}
          </Reveal>
        )}

        <Reveal delay={0.15} className="mt-8 text-center">
          <Link to="/partners" data-testid="home-partners-viewall" className="text-sm font-semibold text-eco-gold transition-colors hover:text-eco-green">
            {t('partners.viewall')} →
          </Link>
        </Reveal>

        <div className="mt-16 rounded-2xl bg-eco-deep px-7 py-12 md:px-12 md:py-16">
          <SectionHeader
            align="center"
            dark
            eyebrow={t('resp.kicker')}
            title={t('resp.title')}
          />
          <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p.keys} delay={i * 0.1} className="text-center">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-white/10 ring-1 ring-white/10">
                  <p.icon size={22} className="text-eco-gold" />
                </div>
                <h3 className="mt-4 text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-white">{t(`${p.keys}.t`)}</h3>
                <p className="eco-secondary mt-2 text-white/55">{t(`${p.keys}.d`)}</p>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1} className="mt-10">
          <div className="flex flex-col items-center gap-5 rounded-2xl border border-eco-gold/30 bg-eco-gold/10 px-7 py-8 text-center md:flex-row md:text-left">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-eco-gold/25 ring-1 ring-eco-gold/40">
              <Handshake size={24} className="text-eco-deep" />
            </span>
            <div className="flex-1">
              <h3 className="text-2xl leading-[1.2] font-semibold tracking-[-0.02em] text-eco-ink">{t('joinus.t')}</h3>
              <p className="eco-secondary mt-2 text-eco-ink/60">
                {t('joinus.d')}
              </p>
            </div>
            <Link
              to="/plan-my-trip?partner=1"
              data-testid="become-partner-cta"
              className="group eco-btn-primary shrink-0"
            >
              {t('cta.become')} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
